import type { EditorState, RemirrorJSON } from '@remirror/core';
import { getRemirrorJSON } from '@remirror/core';
import { createId } from '@paralleldrive/cuid2';
import type * as Img from './interfaces.image';

export class ImageState {
  map = new Map<Base64String, string>();

  cacheImage(result: Pick<Img.ImageAttributes, 'src' | 'fileName'> & Record<string, unknown>) {
    const cache = this.map;

    const alreadyExists = cache.has(result.src);

    const baseAttributes = {
      cid: alreadyExists ? cache.get(result.src) : createId(),
      name: result.fileName,
      src: result.src,
    };

    if (!alreadyExists) {
      cache.set(result.src, baseAttributes.cid);
    }

    const additionalAttributes = { ...result };
    delete additionalAttributes.src;
    delete additionalAttributes.fileName;

    return {
      ...baseAttributes,
      ...additionalAttributes,
    };
  }

  findRenderedImages(editorstate: EditorState) {
    const json = getRemirrorJSON(editorstate);

    return getImages<Base64String>(json).reduce((acc, x) => {
      const exists = acc.find(y => y.cid === x.cid);

      return exists
        ? acc
        : acc.concat(x);
    }, [] as ImageObject<Base64String>[]);
  }

  replaceImageSrc(editorstate: EditorState, lookup: Record<string, URLString>) {
    const json = getRemirrorJSON(editorstate);
    const content = json.content.map(x => replaceImage(x, lookup));

    return {
      ...json,
      content,
    };
  }
}

export type Base64String = string;
export type URLString = string;

type SourceType = Base64String | URLString;

export type ImageObject<T extends SourceType = SourceType> = {
  cid: string;
  src: T;
};

const getImages = <T extends SourceType>(x: RemirrorJSON, acc: ImageObject<T>[] = []): ImageObject<T>[] => {
  if (x.content?.length) {
    return x.content.flatMap(y => getImages(y, acc));
  } else if (x.type === 'image') {
    return [...acc, {
      cid: x.attrs.cid as string,
      src: x.attrs.src as T,
    }];
  } else {
    return acc;
  }
};

const replaceImage = (json: RemirrorJSON, lookup: Record<string, URLString>): RemirrorJSON => {
  if (json.content?.length) {
    return {
      ...json,
      content: json.content.map(x => replaceImage(x, lookup)),
    };
  } else if (json.type === 'image') {
    const src = lookup[json.attrs.cid as string] || json.attrs.src;

    return {
      ...json,
      attrs: {
        ...json.attrs,
        src,
      },
    };
  } else {
    return json;
  }
};